import { useState, useRef, useEffect } from 'react';
import { useAppSelector } from '../store/hooks';
import roleSwitchingService from '../services/roleSwitchingService';
import { useNotification } from './useNotification';

export type Role = 'admin' | 'host' | 'guest' | 'usher';

export interface AvailableRole {
  role: Role;
  label: string;
  hasRole: boolean;
}

export interface UseRoleSwitchingReturn {
  // State
  switchingRole: boolean;
  showRoleSwitchConfirm: boolean;
  targetRole: Role | null;
  showRoleDropdown: boolean;
  roleDropdownRef: React.RefObject<HTMLDivElement | null>;
  
  // Role checks
  hasAdminRole: boolean;
  hasHostRole: boolean;
  hasGuestRole: boolean;
  hasUsherRole: boolean;
  currentRole: Role;
  
  // Available roles (excluding current role, in standardized order)
  availableRoles: AvailableRole[];
  
  // Actions
  setShowRoleDropdown: (show: boolean) => void;
  setShowRoleSwitchConfirm: (show: boolean) => void;
  setTargetRole: (role: Role | null) => void;
  handleSwitchRoleClick: (role: Role) => void;
  handleSwitchRole: () => Promise<void>;
}

/**
 * Custom hook for managing role switching functionality
 * Provides centralized logic for switching between user roles
 * 
 * @param defaultRole - The default/current role for this dashboard
 * @returns Object containing state, role checks, and handlers
 */
export const useRoleSwitching = (defaultRole: Role): UseRoleSwitchingReturn => {
  const { user } = useAppSelector((state) => state.auth);
  const { showNotification } = useNotification();
  
  // State management
  const [switchingRole, setSwitchingRole] = useState(false);
  const [showRoleSwitchConfirm, setShowRoleSwitchConfirm] = useState(false);
  const [targetRole, setTargetRole] = useState<Role | null>(null);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  // Role checks
  const hasAdminRole = user?.roles?.includes('admin') || false;
  const hasHostRole = user?.roles?.includes('host') || false;
  const hasGuestRole = user?.roles?.includes('guest') || false;
  const hasUsherRole = user?.roles?.includes('usher') || false;
  const currentRole = (user?.active_role || user?.role || defaultRole) as Role;

  // Available roles in standardized order: admin, host, guest, usher
  // Exclude current role from the list
  const availableRoles: AvailableRole[] = [
    { role: 'admin' as Role, label: 'Admin Dashboard', hasRole: hasAdminRole },
    { role: 'host' as Role, label: 'Host Dashboard', hasRole: hasHostRole },
    { role: 'guest' as Role, label: 'Guest Dashboard', hasRole: hasGuestRole },
    { role: 'usher' as Role, label: 'Usher Dashboard', hasRole: hasUsherRole },
  ].filter(r => r.hasRole && r.role !== currentRole);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setShowRoleDropdown(false);
      }
    };

    if (showRoleDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showRoleDropdown]);

  // Handle role switching - show confirmation first
  const handleSwitchRoleClick = (role: Role) => {
    // Validate role availability
    const roleChecks: Record<Role, boolean> = {
      admin: hasAdminRole,
      host: hasHostRole,
      guest: hasGuestRole,
      usher: hasUsherRole,
    };

    if (!roleChecks[role]) {
      showNotification(`You do not have ${role} role`, 'error');
      return;
    }

    setTargetRole(role);
    setShowRoleSwitchConfirm(true);
  };

  // Actually perform the role switch after confirmation
  const handleSwitchRole = async () => {
    if (!targetRole) return;

    try {
      setSwitchingRole(true);
      setShowRoleSwitchConfirm(false);
      await roleSwitchingService.switchRole(targetRole);
      // Force a full page reload to ensure URL updates and all components use the new role
      // This prevents race conditions with Redux state updates and routing
      setTimeout(() => {
        window.location.href = `/${targetRole}/dashboard`;
      }, 100);
    } catch (error: any) {
      showNotification(error.message || 'Failed to switch role', 'error');
      setSwitchingRole(false);
      setTargetRole(null);
    }
  };

  return {
    // State
    switchingRole,
    showRoleSwitchConfirm,
    targetRole,
    showRoleDropdown,
    roleDropdownRef,
    
    // Role checks
    hasAdminRole,
    hasHostRole,
    hasGuestRole,
    hasUsherRole,
    currentRole,
    
    // Available roles
    availableRoles,
    
    // Actions
    setShowRoleDropdown,
    setShowRoleSwitchConfirm,
    setTargetRole,
    handleSwitchRoleClick,
    handleSwitchRole,
  };
};

